/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/pwa/pushLink.ts
 *
 * Rôle :
 *   « Relier mon abonnement » : après la connexion d une personne, envoyer
 *   au serveur (push/link.php) l endpoint de l abonnement Web Push présent
 *   dans ce navigateur, pour qu il soit rattaché à la personne
 *   (`tne_push_subscriptions.visitor_id`). CV Matching, vague 4, point
 *   ouvert de la vague 3 : sans ce lien, les rappels d entretien par push ne
 *   trouvaient aucun abonnement.
 *
 *   Appelé par useVisitorSession dès que la sonde de session répond
 *   « connecté ». Jamais bloquant : un navigateur sans Web Push, sans
 *   abonnement, hors ligne, ou un serveur qui refuse (404 : pas encore
 *   souscrit ; 403 : emprunt d identité) ne produisent aucune erreur
 *   visible. Une seule tentative par session de navigation et par endpoint :
 *   le serveur est idempotent, mais on ne le sollicite pas à chaque page.
 *
 * Mikael - chef de projet - 2026-09-20
 */

import { apiRequest } from '@/api/client';

/** Endpoints déjà envoyés pendant cette session de navigation. */
const dejaRelies = new Set<string>();

/** Pour les tests : oublier ce qui a déjà été relié. */
export function reinitialiserLiaisonPush(): void {
  dejaRelies.clear();
}

/**
 * Lit l abonnement push du navigateur, s il existe, et le relie à la
 * personne connectée. Résout `true` si le serveur a relié (ou avait déjà
 * relié), `false` sinon, sans jamais rejeter.
 */
export async function relierAbonnementPush(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
    if (typeof window === 'undefined' || !('PushManager' in window)) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub === null || !sub.endpoint) return false;
    if (dejaRelies.has(sub.endpoint)) return true;
    dejaRelies.add(sub.endpoint);
    const r = await apiRequest<{ ok?: boolean; linked?: boolean }>('push/link.php', {
      method: 'POST',
      body: { endpoint: sub.endpoint },
    });
    return r?.ok === true;
  } catch {
    // Silence voulu : relier un abonnement est une commodité, pas un parcours.
    return false;
  }
}
